import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { Box, ButtonMantine, Group, Modal, Stack, Transition, notifications } from 'ui';

import { useFormListValidation } from '../../../..';
import { useShowAccountCodeByCode, useShowAccountCodeById } from '../../../../api';
import { getErrorMessage_v2 } from '../../../../utils';
import { AccountCodeSearchSelectWrapper } from './AccountCodeSearchSelect';
import { AssignAccountCodeInfoBox } from './AssignAccountCodeInfoBox';
import {
  AccountCodeFeatures,
  EditRemoveAccountModalBaseProp,
  ProductItemProps,
} from './AssignAccountCodes.types';
import { HistoricalTransactionRadio } from './HistoricalTransactionRadio';
import { NewOldAccountCodeDisplay } from './NewOldAccountCodeDisplay';
import { RemoveAccountCodeModal } from './RemoveAccountCodeModal';
import {
  invalidationKey,
  useAssignAccountCodes,
  useGetHistoricalTransactionCount,
  useUpdateHistoricalTransactions,
} from './hooks';
import { AccountCodeSearchForm } from './types';

interface EditAccountCodeModalProps extends EditRemoveAccountModalBaseProp {
  hasWriteAccess?: boolean;
  /** Called when the account code is updated successfully */
  onSuccess?: () => void;
}

type EditAccountCodeSteps = 'edit_account_code' | 'edit_historical_transactions';
type EditAccountCodeContentStepOneProps = {
  causeId: string;
  accountCode?: string | null;
  setStep: (step: EditAccountCodeSteps) => void;
  feature: AccountCodeFeatures;
  storeItemId?: string;
};

type EditAccountCodeContentStepTwoProps = {
  causeId: string;
  accountCode?: string | null;
  storeItemId?: string;
  feature: AccountCodeFeatures;
};

interface EditAccountCodeContentFooterProps extends EditAccountCodeContentStepOneProps {
  step: EditAccountCodeSteps;
  onClose: () => void;
  setIsLoading: (isLoading: boolean) => void;
  productItemProps?: ProductItemProps;
  /** Called when the account code is updated successfully */
  onSuccess?: () => void;
}

const EditAccountCodeContentFooter = ({
  onClose,
  setStep,
  accountCode,
  causeId,
  feature,
  storeItemId,
  step,
  setIsLoading,
  productItemProps,
  onSuccess,
}: EditAccountCodeContentFooterProps) => {
  const [isRemoveModalOpen, setIsRemoveModalOpen] = useState(false);

  const { useGetForm } = useFormListValidation<AccountCodeSearchForm>();
  const form = useGetForm('account_code');
  const editHistoricalForm = useGetForm('edit_historical_transaction');

  const { data: historicalTransactionCount } = useGetHistoricalTransactionCount({
    feature,
    causeId,
    storeItemId,
  });

  const { data: newAccountCode } = useShowAccountCodeById({
    causeId,
    codeId: form?.values?.accountCode,
  });

  const { mutate: updateAccountCode, isLoading: isAssigninLoading } = useAssignAccountCodes({
    feature,
  });

  const { mutate: updateHistoricalTransactions, isLoading: isUpdateHistorical } =
    useUpdateHistoricalTransactions({ feature });

  const isLoading = useMemo(
    () => isAssigninLoading || isUpdateHistorical,
    [isAssigninLoading, isUpdateHistorical]
  );

  const queryClient = useQueryClient();
  const handleSaveSuccess = useCallback(() => {
    onClose();
    notifications.show({
      message: 'Account code updated successfully',
      variant: 'success',
    });
    void queryClient.invalidateQueries({
      queryKey: invalidationKey({ feature, causeId, storeItemId }),
    });
    onSuccess?.();
  }, [onClose, queryClient, feature, causeId, storeItemId, onSuccess]);

  const handleUpdate = useCallback(
    (toUpdateHistoricalTransactions?: boolean) => {
      if (newAccountCode && historicalTransactionCount && historicalTransactionCount.count > 0) {
        setStep('edit_historical_transactions');
      }

      const accountCode =
        feature === 'shop_to_give'
          ? newAccountCode?.account_code?.uuid
          : newAccountCode?.account_code?.code;

      updateAccountCode(
        { accountCode, causeId, storeItemId },
        {
          onSuccess() {
            if (toUpdateHistoricalTransactions) {
              updateHistoricalTransactions(
                { accountCodeId: newAccountCode?.account_code?.uuid, causeId, storeItemId },
                {
                  onSuccess() {
                    handleSaveSuccess();
                  },
                  onError(error) {
                    notifications.show({
                      message: getErrorMessage_v2(error),
                      variant: 'error',
                    });
                  },
                }
              );
            } else {
              handleSaveSuccess();
            }
          },
          onError(error) {
            notifications.show({
              message: getErrorMessage_v2(error),
              variant: 'error',
            });
          },
        }
      );
    },
    [
      newAccountCode,
      historicalTransactionCount,
      feature,
      updateAccountCode,
      causeId,
      storeItemId,
      setStep,
      updateHistoricalTransactions,
      handleSaveSuccess,
    ]
  );

  const onSaveClick = useCallback(() => {
    if (step === 'edit_account_code') {
      if (historicalTransactionCount && historicalTransactionCount.count > 0) {
        return setStep?.('edit_historical_transactions');
      } else {
        return handleUpdate();
      }
    }

    if (step === 'edit_historical_transactions') {
      const isAppliedToAllTransactions = editHistoricalForm?.values?.applyTo === 'all';
      return handleUpdate(isAppliedToAllTransactions);
    }
  }, [
    historicalTransactionCount,
    editHistoricalForm?.values?.applyTo,
    handleUpdate,
    setStep,
    step,
  ]);

  useEffect(() => {
    setIsLoading(isLoading);
  }, [isLoading, setIsLoading]);

  return (
    <>
      {step == 'edit_account_code' ? (
        <Group justify={accountCode ? 'space-between' : 'flex-end'}>
          {accountCode && (
            <ButtonMantine
              size="md"
              variant="subtle"
              color="cerise"
              onClick={() => setIsRemoveModalOpen(true)}
            >
              Remove Code
            </ButtonMantine>
          )}
          <Group>
            <ButtonMantine size="md" variant="subtle" onClick={onClose}>
              Cancel
            </ButtonMantine>
            <ButtonMantine
              size="md"
              onClick={onSaveClick}
              disabled={
                !newAccountCode?.account_code?.code ||
                accountCode == newAccountCode?.account_code?.code
              }
              loading={isLoading}
            >
              {accountCode ? 'Save Changes' : 'Save'}
            </ButtonMantine>
          </Group>
        </Group>
      ) : (
        <Group justify="flex-end">
          <ButtonMantine size="md" variant="subtle" onClick={() => setStep('edit_account_code')}>
            Cancel
          </ButtonMantine>
          <ButtonMantine size="md" onClick={onSaveClick} loading={isLoading}>
            Apply
          </ButtonMantine>
        </Group>
      )}

      <RemoveAccountCodeModal
        opened={isRemoveModalOpen}
        onClose={() => setIsRemoveModalOpen(false)}
        causeId={causeId}
        storeItemId={storeItemId}
        feature={feature}
        productItemProps={productItemProps}
        accountCode={accountCode}
        onSuccess={() => {
          setIsRemoveModalOpen(false);
          onSuccess?.();
          onClose();
        }}
      />
    </>
  );
};

const EditAccountCodeContentStepTwo = ({
  causeId,
  accountCode,
  storeItemId,
  feature,
}: EditAccountCodeContentStepTwoProps) => {
  const { data: accountCodeData } = useShowAccountCodeByCode({
    causeId,
    accountCode: accountCode,
  });
  const { data: historicalTransactionCount } = useGetHistoricalTransactionCount({
    feature,
    causeId,
    storeItemId,
  });

  return (
    <Stack>
      <NewOldAccountCodeDisplay causeId={causeId} accountCode={accountCodeData} />
      <HistoricalTransactionRadio count={historicalTransactionCount?.count} feature={feature} />
    </Stack>
  );
};

const ViewOnlyFooter = ({ onClose }: { onClose: () => void }) => {
  return (
    <Group justify="flex-end">
      <ButtonMantine size="md" onClick={onClose}>
        Close
      </ButtonMantine>
    </Group>
  );
};
const EditAccountCodeModal = ({
  accountCode,
  causeId,
  storeItemId,
  feature,
  opened,
  onClose,
  productItemProps,
  hasWriteAccess,
  onSuccess,
}: EditAccountCodeModalProps) => {
  const isLoadingRef = useRef(false);

  const [step, setStep] = useState<'edit_account_code' | 'edit_historical_transactions'>(
    'edit_account_code'
  );

  const modalTitle = useMemo(() => {
    if (!hasWriteAccess) {
      return 'View Account Code';
    }
    return accountCode ? 'Edit Account Code' : 'Add Account Code';
  }, [accountCode, hasWriteAccess]);

  const setIsLoading = useCallback((isLoading: boolean) => {
    isLoadingRef.current = isLoading;
  }, []);

  return (
    <Modal
      size="lg"
      opened={opened}
      onClose={() => {
        if (isLoadingRef.current) return;
        setStep('edit_account_code');
        onClose();
      }}
      title={modalTitle}
    >
      <Stack>
        <AssignAccountCodeInfoBox feature={feature} productItemProps={productItemProps} />
        {/* Keep form mounted with no extra visible div */}
        <Transition
          keepMounted
          mounted={step === 'edit_account_code'}
          duration={200}
          exitDuration={0}
          transition={'scale-y'}
        >
          {(styles) => (
            <Box style={styles} w="50%">
              <AccountCodeSearchSelectWrapper
                causeId={causeId}
                accountCode={accountCode}
                disabled={!hasWriteAccess}
              />
            </Box>
          )}
        </Transition>
        <Transition
          mounted={step === 'edit_historical_transactions'}
          transition={'fade'}
          duration={100}
          exitDuration={0}
        >
          {(styles) => (
            <Box style={styles}>
              <EditAccountCodeContentStepTwo
                causeId={causeId}
                accountCode={accountCode}
                feature={feature}
                storeItemId={storeItemId}
              />
            </Box>
          )}
        </Transition>
        {hasWriteAccess ? (
          <EditAccountCodeContentFooter
            onClose={() => {
              setStep('edit_account_code');
              onClose();
            }}
            setStep={setStep}
            accountCode={accountCode}
            causeId={causeId}
            feature={feature}
            storeItemId={storeItemId}
            step={step}
            setIsLoading={setIsLoading}
            productItemProps={productItemProps}
            onSuccess={onSuccess}
          />
        ) : (
          <ViewOnlyFooter onClose={onClose} />
        )}
      </Stack>
    </Modal>
  );
};

export { EditAccountCodeModal };
